import { Link, NavLink } from "react-router-dom";
import { useMemo, useState } from "react";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { motion, AnimatePresence } from "framer-motion";
import { useSite } from "@/contexts/SiteContent";
import { resolveAssetUrl } from "@/lib/api";

function buildTree(menu) {
  const roots = menu.filter(m => !m.parent);
  return roots.map(r => ({ ...r, children: menu.filter(c => c.parent === r.slug).sort((a,b)=>a.order-b.order) })).sort((a,b)=>a.order-b.order);
}
function routeFor(item) {
  if (item.external_url) return item.external_url;
  if (item.slug === "home") return "/";
  if (["about","products","services","partners","contact","emi-calculator","gallery"].includes(item.slug)) return `/${item.slug}`;
  return `/p/${item.slug}`;
}

function DesktopItem({ item }) {
  const [open, setOpen] = useState(false);
  if (item.children?.length) {
    return (
      <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        <NavLink to={routeFor(item)} data-testid={`nav-${item.slug}`}
          className={({ isActive }) => `flex items-center gap-1 text-sm font-semibold transition-colors whitespace-nowrap py-1.5 ${isActive ? "text-saffron" : "text-navy hover:text-saffron"}`}>
          {item.title} <ChevronDown className="h-4 w-4" strokeWidth={1.8} />
        </NavLink>
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18 }} className="absolute left-0 top-full pt-3 z-50">
              <div className="min-w-[240px] rounded-2xl border border-slate-200 bg-white shadow-xl p-2">
                {item.children.map(c => (
                  <NavLink key={c.slug} to={routeFor(c)} data-testid={`nav-${c.slug}`}
                    className="block px-4 py-2.5 rounded-xl text-sm font-medium text-navy hover:bg-saffron/10 hover:text-saffron transition-colors whitespace-nowrap">{c.title}</NavLink>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
  return (
    <NavLink to={routeFor(item)} data-testid={`nav-${item.slug}`}
      className={({ isActive }) => `underline-anim text-sm font-semibold transition-colors whitespace-nowrap py-1.5 ${isActive ? "text-saffron" : "text-navy hover:text-saffron"}`}>
      {item.title}
    </NavLink>
  );
}

function MobileItem({ item, onClose }) {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children?.length > 0;
  return (
    <div className="py-1">
      <div className="flex items-center">
        <NavLink to={routeFor(item)} data-testid={`nav-mobile-${item.slug}`} onClick={onClose}
          className="flex-1 block px-3 py-3 rounded-xl text-base font-semibold hover:bg-saffron/10 hover:text-saffron text-navy">
          {item.title}
        </NavLink>
        {hasChildren && (
          <button type="button" data-testid={`nav-mobile-${item.slug}-toggle`}
            aria-label={`Toggle ${item.title} submenu`} onClick={() => setOpen(o => !o)}
            className="h-10 w-10 grid place-items-center rounded-xl text-ink-muted hover:bg-saffron/10 hover:text-saffron">
            <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.8}/>
          </button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {hasChildren && open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
            <div className="pl-3 border-l border-slate-200 ml-3 my-1">
              {item.children.map(c => (
                <NavLink key={c.slug} to={routeFor(c)} data-testid={`nav-mobile-${c.slug}`} onClick={onClose}
                  className="block px-3 py-2 rounded-lg text-sm text-ink-muted hover:text-saffron">{c.title}</NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const { content, menu } = useSite();
  const brand = content?.branding || {};
  const hdr = content?.header || {};
  const tree = useMemo(() => buildTree(menu), [menu]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const logoUrl = resolveAssetUrl(brand.logo_url || "");
  const logoH = brand.logo_height || 44;
  const siteName = brand.site_name || "TARA Finserv";
  const tagline = brand.tagline || "Trusted finance partner";
  const phone = hdr.phone || "+91 98765 43210";
  const phoneDigits = phone.replace(/\s/g, "");
  const callLabel = hdr.call_label || "Call Support";

  return (
    <header className="sticky top-0 z-40 glass-nav">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 xl:px-16">
        <div className="h-18 sm:h-20 flex items-center justify-between gap-4 sm:gap-6 py-2">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 shrink-0">
            {logoUrl && <img src={logoUrl} alt={siteName} style={{ height: `${logoH}px` }} className="w-auto object-contain"/>}
            <div className="leading-tight hidden md:block">
              <div className="text-[1rem] sm:text-[1.05rem] font-extrabold tracking-[0.04em]">
                {(() => {
                  const parts = siteName.trim().split(/\s+/);
                  const first = parts.shift() || "";
                  const rest = parts.join(" ");
                  return (<>
                    <span className="text-navy">{first}</span>
                    {rest && <> <span className="text-saffron">{rest}</span></>}
                  </>);
                })()}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-ink-muted mt-1">{tagline}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-5 flex-1 justify-center">
            {tree.map(it => <DesktopItem key={it.slug} item={it} />)}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {hdr.show_call_button !== false && (
              <a href={`tel:${phoneDigits}`} data-testid="nav-call-cta"
                 className="group hidden md:inline-flex items-center gap-3 rounded-full bg-saffron text-white pl-2 pr-5 h-12 hover:bg-saffron-600 transition-all shadow-[0_10px_24px_-12px_rgba(242,117,15,0.6)]">
                <span className="h-9 w-9 rounded-full bg-navy grid place-items-center group-hover:scale-110 transition-transform">
                  <Phone className="h-4 w-4 text-saffron" strokeWidth={2.3}/>
                </span>
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/85">{callLabel}</div>
                  <div className="text-sm font-semibold whitespace-nowrap text-white">{phone}</div>
                </div>
              </a>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="nav-mobile-trigger" className="lg:hidden p-2 -mr-2">
                  {mobileOpen ? <X className="h-6 w-6"/> : <Menu className="h-6 w-6"/>}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85%] sm:w-[400px] p-0">
                <div className="p-5 border-b border-slate-200 flex items-center gap-2">
                  {logoUrl && <img src={logoUrl} alt={siteName} style={{ height: "36px" }}/>}
                  <span className="font-display font-extrabold text-navy">{siteName}</span>
                </div>
                <div className="p-3 overflow-y-auto h-[calc(100vh-180px)]">
                  {tree.map(it => <MobileItem key={it.slug} item={it} onClose={() => setMobileOpen(false)} />)}
                </div>
                <div className="p-4 border-t border-slate-200">
                  <a href={`tel:${phoneDigits}`} className="btn-saffron rounded-full h-12 w-full inline-flex items-center justify-center gap-2 font-semibold">
                    <Phone className="h-4 w-4"/> {callLabel} · {phone}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
